import { FormattedMessage, useIntl } from 'react-intl';

import LabeledValue from '#components/form/LabeledValue.tsx';
import { ChargingDetailsView } from '#components/tourStops/ChargingDetailsView.tsx';
import type { Stop } from '#domain/Stop.ts';
import { useFormatDateTime } from '#hooks/shared/useFormatDateTime.ts';

export type OrderStopFields = Stop & {
    comment?: string;
};

type OrderStopFormProps = {
    orderStop: OrderStopFields;
};

const sectionClasses = 'display-flex flex-column gap-10 bg-lightest padding-10 rounded';
const columnClasses = 'display-grid gap-10';

export const OrderStopForm = ({ orderStop }: OrderStopFormProps) => {
    const intl = useIntl();
    const { formatDateTime } = useFormatDateTime();

    const window =
        orderStop.type === 'load'
            ? 'intl-msg:ordercom-cloud-frontend.orders.details.stop.pick-up.window'
            : 'intl-msg:ordercom-cloud-frontend.orders.details.stop.delivery.window';
    return (
        <div data-testid={`order-stop-form-${orderStop.type}-${orderStop.id}`} className={sectionClasses}>
            {orderStop.charging && <ChargingDetailsView value={orderStop.charging} />}
            <LabeledValue
                boxed
                label={intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.orders.details.stop.form.input.company',
                })}
                value={orderStop.name}
                icon={'building'}
            />
            <div className={`${columnClasses} grid-cols-3`}>
                <LabeledValue
                    boxed
                    className={'grid-colspan-2'}
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.orders.details.stop.form.input.street',
                    })}
                    value={orderStop.location.street}
                />
                <LabeledValue
                    boxed
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.orders.details.stop.form.input.country',
                    })}
                    value={orderStop.location.country}
                />
            </div>
            <div className={`${columnClasses} grid-cols-3`}>
                <LabeledValue
                    boxed
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.orders.details.stop.form.input.zip-code',
                    })}
                    value={orderStop.location.zipCode}
                />
                <LabeledValue
                    boxed
                    className={'grid-colspan-2'}
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.orders.details.stop.form.input.city',
                    })}
                    value={orderStop.location.city}
                />
            </div>
            <div>
                <div className='bg-white padding-x-15 padding-y-10 shadow-subtle rounded'>
                    <div className='text-color-dark margin-bottom-0'>
                        <FormattedMessage id={window} />
                    </div>
                    <div className=' display-grid grid-cols-2'>
                        <LabeledValue
                            label={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.orders.details.stop.form.input.from',
                            })}
                            value={formatDateTime(orderStop.startTarget)}
                        />
                        <LabeledValue
                            label={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.orders.details.stop.form.input.to',
                            })}
                            value={formatDateTime(orderStop.endTarget)}
                        />
                    </div>
                </div>
            </div>
            {orderStop.comment ? (
                <LabeledValue
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.orders.details.stop.form.input.comment',
                    })}
                    className='padding-x-15 padding-y-5'
                    icon='comment'
                    value={orderStop.comment}
                />
            ) : null}
        </div>
    );
};
